import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession as amplifyFetchAuthSession,
  confirmSignIn as amplifyConfirmSignIn,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  SignInOutput,
} from "@aws-amplify/auth";
import { COGNITO_USER_POOL_ID, COGNITO_CLIENT_ID } from "./config";

// Detect if we should use offline mock authentication for local development (ADR-1.4)
const poolId = (COGNITO_USER_POOL_ID as string) || "";
export const IS_LOCAL_MOCK =
  process.env.NODE_ENV === "development" && (!poolId || poolId === "MOCK");

const MOCK_STORAGE_KEY = "angel_tree_dev_mock_admin";

if (!IS_LOCAL_MOCK && COGNITO_USER_POOL_ID && COGNITO_CLIENT_ID) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO_USER_POOL_ID,
        userPoolClientId: COGNITO_CLIENT_ID,
      },
    },
  });
}

export interface AdminUser {
  username: string;
  email: string;
}

export async function getAuthToken(): Promise<string | null> {
  if (IS_LOCAL_MOCK) {
    const isMockLoggedIn = localStorage.getItem(MOCK_STORAGE_KEY) === "true";
    return isMockLoggedIn ? "mock-dev-token" : null;
  }
  try {
    const session = await amplifyFetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getCurrentAdmin(): Promise<AdminUser | null> {
  if (IS_LOCAL_MOCK) {
    const isMockLoggedIn = localStorage.getItem(MOCK_STORAGE_KEY) === "true";
    if (!isMockLoggedIn) return null;
    return {
      username: "dev-admin@giving-trees.com",
      email: "dev-admin@giving-trees.com",
    };
  }
  try {
    const user = await amplifyGetCurrentUser();
    return {
      username: user.username,
      email: user.signInDetails?.loginId || user.username,
    };
  } catch {
    return null;
  }
}

export async function loginAdmin(email: string, password: string): Promise<SignInOutput> {
  if (IS_LOCAL_MOCK) {
    if (password.toLowerCase().includes("temp")) {
      return {
        isSignedIn: false,
        nextStep: { signInStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" },
      };
    }
    localStorage.setItem(MOCK_STORAGE_KEY, "true");
    return {
      isSignedIn: true,
      nextStep: { signInStep: "DONE" },
    };
  }
  return await amplifySignIn({ username: email.trim(), password });
}

export async function confirmFirstTimePassword(newPassword: string): Promise<SignInOutput> {
  if (IS_LOCAL_MOCK) {
    localStorage.setItem(MOCK_STORAGE_KEY, "true");
    return {
      isSignedIn: true,
      nextStep: { signInStep: "DONE" },
    };
  }
  return await amplifyConfirmSignIn({ challengeResponse: newPassword });
}

export async function logoutAdmin(): Promise<void> {
  if (IS_LOCAL_MOCK) {
    localStorage.removeItem(MOCK_STORAGE_KEY);
    return;
  }
  try {
    await amplifySignOut();
  } catch (err) {
    console.error("Sign out error:", err);
  }
}

export async function requestPasswordReset(email: string) {
  if (IS_LOCAL_MOCK) {
    return { isPasswordReset: false, nextStep: { resetPasswordStep: "CONFIRM_RESET_PASSWORD_WITH_CODE" } };
  }
  return await amplifyResetPassword({ username: email.trim() });
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  if (IS_LOCAL_MOCK) {
    return;
  }
  return await amplifyConfirmResetPassword({
    username: email.trim(),
    confirmationCode: code.trim(),
    newPassword,
  });
}
